<template>
    <div class="developer-notes row">
      <!-- Timeline/List Toggle + Timeline or Liste Section -->
      <div class="col-12 mb-3" :class="{'col-md-8': isDeveloper}">
        <b-card class="mb-1">
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="isListMode"
              class="custom-control-success"
              name="check-button"
              switch
              switch-variant="success"
            >
              <span class="switch-icon-left">
                <feather-icon icon="ListIcon" />
              </span>
              <span class="switch-icon-right">
                <feather-icon icon="CalendarIcon" />
              </span>
            </b-form-checkbox>
            <span class="ml-2">{{ isListMode ? 'Liste' : 'Tarihsel' }}</span>
          </div>
        </b-card>
        <!-- Tarihsel Görünüm -->
        <template v-if="!isListMode">
          <div v-if="notesByDate.length">
            <div
              v-for="group in notesByDate"
              :key="group.date"
              class="mb-4"
            >
              <b-card
                :title="formatDate(group.date)"
                class="mb-2"
                header-bg-variant="light"
                header-text-variant="dark"
              >
                <app-timeline>
                  <app-timeline-item
                    v-for="note in group.notes"
                    :key="note.id"
                    :variant="getVariant(note.type)"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="timeline-time small">{{ formatRelativeTime(note.date) }}</div>
                        <div class="timeline-title font-weight-bold">{{ note.title }}</div>
                        <div class="timeline-subtitle text-muted">{{ note.description }}</div>
                      </div>
                      <div class="d-flex align-items-center" v-if="isDeveloper">
                        <b-button variant="primary" size="sm" class="mr-1" @click="editNote(note)">
                          <feather-icon icon="Edit2Icon" title="Düzenle" />
                        </b-button>
                        <b-button variant="danger" size="sm" @click="deleteNote(note.id)">
                          <feather-icon icon="Trash2Icon" title="Sil" />
                        </b-button>
                      </div>
                    </div>
                  </app-timeline-item>
                </app-timeline>
              </b-card>
            </div>
          </div>
          <div v-else>
            <app-timeline>
              <app-timeline-item
                title="Not bulunamadı"
                subtitle="Henüz hiç geliştirme notu eklenmemiş."
                :time="''"
                variant="secondary"
              />
            </app-timeline>
          </div>
        </template>
        <!-- Liste (Klasik) Görünüm -->
        <template v-else>
          <b-card>
            <app-timeline>
              <app-timeline-item
                v-for="note in notes"
                :key="note.id"
                :variant="getVariant(note.type)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="timeline-time small">{{ formatRelativeTime(note.date) }}</div>
                    <div class="timeline-title font-weight-bold">{{ note.title }}</div>
                    <div class="timeline-subtitle text-muted">{{ note.description }}</div>
                  </div>
                  <div class="d-flex align-items-center" v-if="isDeveloper">
                    <b-button variant="primary" size="sm" class="mr-1" @click="editNote(note)">
                      <feather-icon icon="Edit2Icon" title="Düzenle" />
                    </b-button>
                    <b-button variant="danger" size="sm" @click="deleteNote(note.id)">
                      <feather-icon icon="Trash2Icon" title="Sil" />
                    </b-button>
                  </div>
                </div>
              </app-timeline-item>
              <app-timeline-item
                v-if="notes.length === 0"
                title="Not bulunamadı"
                subtitle="Henüz hiç geliştirme notu eklenmemiş."
                :time="''"
                variant="secondary"
              />
            </app-timeline>
          </b-card>
        </template>
      </div>
      <!-- Add/Edit Note Section -->
      <div class="col-12 col-md-4" v-if="isDeveloper">
        <b-card>
          <div class="d-flex justify-content-center">
            <feather-icon
              class="cursor-pointer"
              icon="PlusSquareIcon"
              size="25"
              @click="showAdd = true; resetForm()"
              v-if="!showAdd && !isEditing"
            />
          </div>
          <b-collapse v-model="showAdd">
            <b-form @submit.prevent="isEditing ? updateNote() : addNote()">
              <b-form-group label="Tarih" label-for="dev-note-date">
                <b-form-datepicker
                  id="dev-note-date"
                  v-model="newNote.date"
                  locale="tr"
                  :date-format-options="{ year: 'numeric', month: 'long', day: 'numeric' }"
                  placeholder="Tarih seç"
                />
              </b-form-group>
              <b-form-group label-for="dev-note-type">
                <b-form-radio-group
                  v-model="newNote.type"
                  name="dev-note-type"
                  class="demo-inline-spacing"
                  stacked
                >
                  <b-form-radio
                    :value="1"
                    class="custom-control-success"
                  >
                    <span class="radio-label-success">Geliştirme</span>
                  </b-form-radio>
                  <b-form-radio
                    :value="0"
                    class="custom-control-warning"
                  >
                    <span class="radio-label-warning">Bug Giderme</span>
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
              <b-form-group label="Başlık" label-for="dev-note-title">
                <b-form-input
                  id="dev-note-title"
                  v-model="newNote.title"
                  required
                  maxlength="255"
                />
              </b-form-group>
              <b-form-group label="Açıklama" label-for="dev-note-description">
                <b-form-textarea
                  id="dev-note-description"
                  v-model="newNote.description"
                  rows="3"
                />
              </b-form-group>
              <b-button type="submit" :variant="isEditing ? 'primary' : 'success'" block>
                {{ isEditing ? 'Güncelle' : 'Kaydet' }}
              </b-button>
              <b-button
                type="button"
                variant="danger"
                block
                @click="cancelEdit"
              >Vazgeç</b-button>
            </b-form>
          </b-collapse>
        </b-card>
      </div>
    </div>
  </template>
  
  <script>
  import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
  import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  import {
    BButton,
    BIcon,
    BCard,
    BCardText,
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BFormRadioGroup,
    BFormRadio,
    BFormCheckbox,
    BCollapse,
    BFormDatepicker
  } from 'bootstrap-vue'
  
  export default {
    name: 'DeveloperNotes',
    components: {
      AppTimeline,
      AppTimelineItem,
      BButton,
      BIcon,
      BCard,
      BCardText,
      BForm,
      BFormInput,
      BFormGroup,
      BFormTextarea,
      BFormRadioGroup,
      BFormRadio,
      BFormCheckbox,
      BCollapse,
      BFormDatepicker
    },
    data() {
      return {
        notes: [],
        showAdd: false,
        isListMode: false,
        isEditing: false,
        editNoteId: null,
        newNote: {
          date: this.today(),
          title: '',
          description: '',
          type: 1,
        },
        loading: false,
      }
    },
    computed: {
      notesByDate() {
        if (!this.notes.length) return []
        const groupObj = {}
        this.notes.forEach(note => {
          if (!groupObj[note.date]) groupObj[note.date] = []
          groupObj[note.date].push(note)
        })
        return Object.keys(groupObj)
          .sort((a, b) => new Date(b) - new Date(a))
          .map(date => ({
            date,
            notes: groupObj[date],
          }))
      },
    },
    created() {
      this.fetchNotes()
    },
    methods: {
      today() {
        const d = new Date()
        return d.toISOString().substr(0, 10)
      },
      async fetchNotes() {
        this.loading = true
        try {
          const response = await this.$http.get('/manage/notes')
          const data = response.body || response.data
          if (data.success) {
            this.notes = (data.notes || []).map(n => ({
              ...n,
              type: typeof n.type !== 'undefined' ? Number(n.type) : 1,
            }))
          } else {
            this.notes = []
          }
        } catch (e) {
          this.notes = []
        }
        this.loading = false
      },
      async addNote() {
        if (!this.newNote.title) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Hata',
              icon: 'CheckIcon',
              variant: 'danger',
              text: `Başlık Zorunlu`,
            },
          })
          return false
        }
  
        try {
          const response = await this.$http.post('/manage/notes', this.newNote)
          const data = response.body || response.data
          if (data.success) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Not Eklendi',
                icon: 'CheckIcon',
                variant: 'success',
                text: `Yeni not eklendi`,
              },
            })
            this.notes.unshift({
              id: data.id,
              date: data.date,
              title: data.title,
              description: data.description,
              type: this.newNote.type,
            })
            this.resetForm()
            this.showAdd = false
          } else {
            alert(data.error || 'Bir hata oluştu.')
          }
        } catch (e) {
          alert('Bir hata oluştu.')
        }
      },
      editNote(note) {
        this.isEditing = true
        this.editNoteId = note.id
        this.newNote = {
          date: note.date,
          title: note.title,
          description: note.description,
          type: note.type,
        }
        this.showAdd = true
      },
      async updateNote() {
        if (!this.newNote.title) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Hata',
              icon: 'CheckIcon',
              variant: 'danger',
              text: `Başlık Zorunlu`,
            },
          })
          return false
        }
        try {
          const response = await this.$http.put('/manage/notes', {
            id: this.editNoteId,
            ...this.newNote,
          })
          const data = response.body || response.data
          if (data.success) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Not Güncellendi',
                icon: 'Edit2Icon',
                variant: 'primary',
                text: `Not güncellendi`,
              },
            })
            // Listeyi güncelle
            const idx = this.notes.findIndex(n => n.id === this.editNoteId)
            if (idx !== -1) {
              this.$set(this.notes, idx, {
                ...this.notes[idx],
                ...this.newNote,
                id: this.editNoteId,
              })
            }
            this.resetForm()
            this.showAdd = false
            this.isEditing = false
            this.editNoteId = null
          } else {
            alert(data.error || 'Güncellenemedi.')
          }
        } catch (e) {
          alert('Bir hata oluştu.')
        }
      },
      cancelEdit() {
        this.resetForm()
        this.isEditing = false
        this.editNoteId = null
        this.showAdd = false
      },
      async deleteNote(id) {
        if (!confirm('Bu notu silmek istediğinize emin misiniz?')) return
        try {
          const response = await this.$http.delete(`/manage/notes/${id}`)
          const data = response.body || response.data
          if (data.success) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Not silindi',
                icon: 'Trash2Icon',
                variant: 'danger',
                text: `Not silindi`,
              },
            })
            this.notes = this.notes.filter(note => note.id !== id)
          } else {
            alert(data.error || 'Silinemedi.')
          }
        } catch (e) {
          alert('Bir hata oluştu.')
        }
      },
      formatRelativeTime(dateStr) {
        const date = new Date(dateStr)
        const now = new Date()
        const diffMs = now - date
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        if (diffDays === 0) return 'Bugün'
        if (diffDays === 1) return 'Dün'
        if (diffDays < 7) return `${diffDays} gün önce`
        return date.toLocaleDateString('tr-TR')
      },
      getVariant(type) {
        if (type === 0) return 'warning'
        if (type === 1) return 'success'
        return 'primary'
      },
      resetForm() {
        this.newNote = {
          date: this.today(),
          title: '',
          description: '',
          type: 1,
        }
      },
    },
  }
  </script>
  
  <style scoped>
  .developer-notes {
    max-width: 100%;
    margin: 0 auto;
  }
  .radio-label-warning {
    color: #856404;
    font-weight: bold;
  }
  .radio-label-success {
    color: #155724;
    font-weight: bold;
  }
  </style>